import React, { useEffect, useState } from 'react'
import { Box, Button, Card, Dialog, DialogActions, Container, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DataGridHeader } from './components/DataGridHeader'
import LoadingButton from '@mui/lab/LoadingButton'
import { DataGrid } from './DataGrid'
import { Columns } from './Columns'
import moment from 'moment'
import { useTheme } from '@mui/material/styles'
import useResponsive from 'hooks/useResponsive'
import { TreatEmpty } from 'components/TreatEmpty'
import { useNavigate } from 'react-router-dom'

type ProfessionalAthleteProps = {
  professionalAthleteId: number
  name: string
  note: string
  startedAt: string
  dueDateAt: string
  visibleAfterDueDate: boolean
}

type Props = {
  professionalAthletesToAssign: []
  isVisible: boolean
  loading: boolean
  onClose: () => void
  handleAssignRoutineToAthlete: (professionalAthleteProps: ProfessionalAthleteProps[]) => Promise<void>
}

export const ToAssignAthleteModal = ({ professionalAthletesToAssign, isVisible, loading, onClose, handleAssignRoutineToAthlete }: Props) => {
  const { t } = useTranslation()
  const [professionalAthletesSelected, setProfessionalAthletesSelected] = useState<any[]>([])
  const [professionalAthleteToAssignResult, setProfessionalAthleteToAssignResult] = useState<[]>([])
  const mdUp = useResponsive('up', 'md')
  const theme = useTheme()
  const navigate = useNavigate()

  useEffect(() => {
    setProfessionalAthleteToAssignResult(professionalAthletesToAssign)
  }, [isVisible])

  const handleSearch = (e: any) => {
    if (!e.target.value) {
      setProfessionalAthleteToAssignResult(professionalAthletesToAssign)
      return
    }
    setProfessionalAthleteToAssignResult([])
    const dataFound: [] = []
    const searchText = e.target.value
    const regex = new RegExp(searchText.normalize('NFD').replace(/[\u0300-\u036f]/g, ''), 'i')
    professionalAthletesToAssign.forEach((obj) => {
      let firstName: any = ''
      let surName: any = ''
      Object.entries(obj).forEach(([key, value]) => {
        const textValue: any = value
        if (key === 'athleteName') { firstName = textValue }
        if (key === 'athleteSurname') { surName = textValue }
        if (!dataFound.includes(obj) &&
          (key === 'athleteEmail' || key === 'athleteName' || key === 'athleteSurname') &&
          regex.exec(textValue.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) {
          dataFound.push(obj)
        }
      })
      if (!dataFound.includes(obj) &&
        regex.exec((`${firstName} ${surName}`).normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) {
        dataFound.push(obj)
      }
    })
    setProfessionalAthleteToAssignResult(dataFound)
  }

  const handleAddProfessionalAthletes = async () => {
    const data: ProfessionalAthleteProps[] = []
    professionalAthletesSelected.forEach(function (professionalAthlete) {
      data.push({
        professionalAthleteId: professionalAthlete.id,
        name: professionalAthlete.name,
        note: professionalAthlete.note,
        startedAt: moment(professionalAthlete.startedAt, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        dueDateAt: moment(professionalAthlete.dueDateAt, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        visibleAfterDueDate: professionalAthlete.visibleAfterDueDate
      })
    })
    await handleAssignRoutineToAthlete(data)
    setProfessionalAthletesSelected([])
  }

  const handleAddAthlete = () => navigate('/athletes')

  return (
    <Box>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={isVisible}
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
      >
        <DialogTitle>{`${t('routine.title.assignToAthlete')}`}</DialogTitle>
        <DialogContent>
          <Container>
            <Box marginTop={ 2 }>
              <Card variant="outlined">
                <Stack spacing={1.5} sx={{ padding: { xs: 1, sm: 2 } }}>
                  { professionalAthleteToAssignResult.length === 0
                    ? <TreatEmpty
                      titleMessage={ t('athlete.title.athleteEmpty') }
                      subTitleMessage={ t('athlete.subTitle.athletesEmpty') }
                      buttons={ [{ title: t('athlete.title.addAthlete'), action: handleAddAthlete }] }
                    />
                    : <>
                      <DataGridHeader
                        onActionSearch={(value) => handleSearch(value)}
                      />
                      <Typography color={theme.palette.primary.main} sx={{ pl: '7px', pr: '5px' }}>
                        <b>{t('general.message.editCells')}</b>
                      </Typography>
                      <DataGrid
                        setProfessionalAthletesSelected={setProfessionalAthletesSelected}
                        rows={professionalAthleteToAssignResult}
                        disableColumnMenu={true}
                        initialState={{
                          sorting: {
                            sortModel: [{ field: 'athleteName', sort: 'asc' }]
                          }
                        }}
                        columns={mdUp
                          ? Columns
                          : Columns.filter(
                            (column) => (
                              column.field !== 'name' &&
                              column.field !== 'note' &&
                              column.field !== 'startedAt' &&
                              column.field !== 'dueDateAt' &&
                              column.field !== 'visibleAfterDueDate'
                            )
                          )
                        }
                        hideFooterSelectedRowCount={mdUp ? false : true}
                        sx={{
                          '&.MuiDataGrid-root .css-2ovxb4-MuiContainer-root': {
                            paddingLeft: 0
                          }
                        }}
                      />
                    </>
                  }
                </Stack>
              </Card>
            </Box>
          </Container>
        </DialogContent>
        <DialogActions>
          <LoadingButton loading={loading} variant="contained" onClick={handleAddProfessionalAthletes}>
            {t('general.btn.save')}
          </LoadingButton>
          <Button variant="contained" color="secondary" onClick={onClose}>
            {t('general.btn.dismiss')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
